import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicField, DynamicSections, OptionsInput } from '@cargos/sprintpay-models';
import { DynamicFieldsService } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { ConfigService } from './config.service';
import { CustomerService } from './customer.service';

export interface DynamicFieldsForPayment extends DynamicSections {
    fields?: DynamicField[];
}

export enum TypeDynamicFields {
    AUTO_PAY = 0,
    EXTRA_ITEMS,
}

export interface ColumnsFormat {
    column?: string;
    columnName?: string;
}

export type ValidationAccumulator = {
    [key: string]: {
        value?: string | true | RegExp | string[] | RegExp[];
        errorMessage?: string;
    };
};

@Injectable({
    providedIn: 'root',
})
export class DynamicsFieldsService {
    private fieldsForPayment = new BehaviorSubject<DynamicFieldsForPayment[]>([]);
    private dynamicFieldsForPayment = new BehaviorSubject<DynamicField[]>([]);
    private _collectorForms = new BehaviorSubject<(FormGroup | null)[]>([]);
    private loading = new BehaviorSubject<boolean>(false);

    constructor(
        private dynamicService: DynamicFieldsService,
        private customerService: CustomerService,
        private configService: ConfigService
    ) {}

    getDynamicFieldsByCompany(section?: number): Observable<DynamicField[]> {
        if (this.configService.isEnableRequestorApprovalFlow()) {
            return this.dynamicService.getDynamicFieldsBySection(section).pipe(
                catchError(() => {
                    return of([]);
                })
            );
        }
        return of([]);
    }

    getDynamicSectionsByCompany(companyId?: number): Observable<DynamicSections[]> {
        if (this.configService.isEnableRequestorApprovalFlow()) {
            return this.dynamicService.getDynamicSectionsByCompany(companyId).pipe(
                catchError(() => {
                    return of([]);
                })
            );
        }
        return of([]);
    }

    getDynamicSectionsByDomain(domain: string): Observable<DynamicSections[]> {
        return this.dynamicService.getDynamicSectionsByDomain(domain);
    }

    setLoadingDynamicFields(loading: boolean): void {
        this.loading.next(loading);
    }

    getLoadingDynamicFields(): Observable<boolean> {
        return this.loading.asObservable();
    }

    getDynamicFieldsPerSection(companyId: number | null): Observable<DynamicFieldsForPayment[]> {
        this.setLoadingDynamicFields(true);

        return this.getDynamicSections(companyId).pipe(
            switchMap((dynamicSections: DynamicSections[]) => {
                return this.getDynamicFields().pipe(
                    map((dynamicFields) => {
                        return this.addDynamicFieldsToSection(dynamicSections, dynamicFields);
                    }),
                    map((dynamicFields) => {
                        return this.removeEmptySections(dynamicFields);
                    })
                );
            }),
            map((dynamicFields) => {
                const dynamicFieldsOrdered = this.orderSectionByOrderNumber(dynamicFields);
                this.setDynamicFieldsForPayment(dynamicFieldsOrdered);

                this.setLoadingDynamicFields(false);
                return dynamicFieldsOrdered;
            }),
            catchError((error) => {
                this.setLoadingDynamicFields(false);

                this.setDynamicFieldsForPayment([]);
                return throwError(() => error);
            })
        );
    }

    removeEmptySections(dynamicFields: DynamicFieldsForPayment[]) {
        return dynamicFields.filter((section) => !!section.fields?.length);
    }

    getStaticSections(): Observable<DynamicSections[]> {
        return this.customerService.getCustomerSections();
    }

    getDynamicSections(companyId?: number | null): Observable<DynamicSections[]> {
        if (!!companyId) {
            return this.getDynamicSectionsByCompany(companyId).pipe(
                switchMap((sections) => {
                    if (sections.length) {
                        return of(sections);
                    }
                    return this.getStaticSections();
                })
            );
        }
        return this.getStaticSections();
    }

    filterDuplicatedFields(staticFields: DynamicField[] = [], dynamicFields: DynamicField[] = []): DynamicField[] {
        const newDynamicFields: DynamicField[] = [...dynamicFields];

        staticFields?.forEach((staticField) => {
            if (!dynamicFields?.find((field) => field.name === staticField.name && field.isReplacement)) {
                newDynamicFields.push(staticField);
            }
        });
        return newDynamicFields;
    }

    removeNotAllowedFields(staticFields: DynamicField[] = []): DynamicField[] {
        if (!this.customerService.hasGlCodes()) {
            staticFields = staticFields.filter((staticField) => staticField.name !== 'glCode');
        }

        if (!!this.customerService.getFirstLocationId()) {
            staticFields = staticFields.filter(
                (staticField) => staticField.name !== 'locationId' && staticField.name !== 'locationddId'
            );
        }

        return staticFields;
    }

    setDisplayValue(fields: DynamicField[]): DynamicField[] {
        return fields.map((field) => {
            if (field.fieldType === 'dropdown' || field.fieldType === 'radioButton' || field.fieldType === 'checkbox') {
                const updatedOptions = field.options?.map((option) => {
                    if (option.displayValue == null) {
                        return { ...option, displayValue: option.value };
                    }
                    return option;
                });
                return { ...field, options: updatedOptions };
            }
            return field;
        });
    }

    getDynamicFields(): Observable<DynamicField[]> {
        return this.getDynamicFieldsByCompany().pipe(
            map((fields) => {
                return this.setDisplayValue(fields);
            }),
            switchMap((fields) => {
                // TODO: If we remove the next line, the systme will fail. Review and fix it.
                this.dynamicFieldsForPayment.next(fields);
                return this.getStaticFields().pipe(
                    map((staticFields) => this.filterDuplicatedFields(staticFields, fields)),
                    map((staticFields) => this.removeNotAllowedFields(staticFields))
                );
            })
        );
    }

    getFieldLabelByName(fieldName: string): Observable<string> {
        return this.getDynamicFieldsForPayment$().pipe(
            map((dynamicFieldsForPayment) => {
                let label = '';
                dynamicFieldsForPayment.forEach((section) => {
                    const field = section.fields?.find((field) => field.name === fieldName);
                    if (field) {
                        label = field.label || '';
                        return;
                    }
                });
                return label;
            })
        );
    }

    getFieldByDependencyFieldId(dependentFieldId: number): OptionsInput[] {
        const dynamicFields = this.instant_current_dynamic_fields_for_payment || [];
        return dynamicFields.flatMap((dynamicField) => {
            return dynamicField.fields?.find((field) => field.dependentFieldId === dependentFieldId)?.options || [];
        });
    }

    getStaticFields(): Observable<DynamicField[]> {
        return this.customerService.getCustomerFields();
    }

    addDynamicFieldsToSection(
        dynamicSections: DynamicSections[] = [],
        dynamicFields: DynamicField[] = []
    ): DynamicFieldsForPayment[] {
        const dynamicFieldsForPayment: DynamicFieldsForPayment[] = [];

        if (dynamicSections.length) {
            dynamicSections.forEach((section, index) => {
                dynamicFieldsForPayment[index] = section;

                if (section.id) {
                    const fieldsPerSection = dynamicFields.filter((field) => field.section === section.orderNumber);

                    dynamicFieldsForPayment[index].fields = fieldsPerSection;
                }
            });
        } else if (dynamicFields.length) {
            dynamicFields.forEach((field) => {
                if (!this.getSectionByNumber(dynamicFieldsForPayment, field.section)) {
                    dynamicFieldsForPayment.push({
                        orderNumber: field.section,
                    });
                }

                const section = this.getSectionByNumber(dynamicFieldsForPayment, field.section);

                if (section) {
                    const fieldsPerSection = dynamicFields.filter((field) => field.section === section.orderNumber);
                    section.fields = fieldsPerSection;
                }
            });
        }

        return dynamicFieldsForPayment;
    }

    getSectionByNumber(sections: DynamicFieldsForPayment[], sectionNumber?: number): DynamicFieldsForPayment | null {
        return (
            sections.find((section) => {
                return section.orderNumber === sectionNumber;
            }) || null
        );
    }

    filterDynamicFieldsBySection(dynamicFields: DynamicField[], sectionNumber?: number): DynamicField[] | null {
        return (
            dynamicFields.filter((section) => {
                return section.section === sectionNumber;
            }) || null
        );
    }

    orderSectionByOrderNumber(sections: DynamicFieldsForPayment[] = []): DynamicFieldsForPayment[] {
        try {
            return sections.sort((a, b) => {
                return (a?.['orderNumber'] || 0) - (b?.['orderNumber'] || 0);
            });
        } catch (error) {
            return sections;
        }
    }

    get instant_current_dynamic_fields_for_payment(): DynamicFieldsForPayment[] {
        return this.fieldsForPayment.value;
    }

    setDynamicFieldsForPayment(dynamicFieldsForPayment: DynamicFieldsForPayment[]): void {
        this.fieldsForPayment.next(dynamicFieldsForPayment);
    }

    getDynamicFieldsForPayment$(): Observable<DynamicFieldsForPayment[]> {
        return this.fieldsForPayment.asObservable();
    }

    areFieldsReadyToMakeAPayment$(): Observable<boolean> {
        return this.getDynamicFieldsForPayment$().pipe(map((fields) => !!fields.length));
    }

    getAllDynamicFields(): DynamicField[] {
        return this.dynamicFieldsForPayment.value;
    }

    getAllDynamicFields$(): Observable<DynamicField[]> {
        return this.dynamicFieldsForPayment.asObservable();
    }

    getDynamicFieldsBySection$(sectionNumber: number): Observable<DynamicField[] | null> {
        return this.getAllDynamicFields$().pipe(
            map((fields) => this.filterDynamicFieldsBySection(fields, sectionNumber))
        );
    }

    getAllDynamicFieldsByIdAndNameWithoutReplacement(): DynamicField[] {
        return this.getAllDynamicFields()
            ?.filter((item) => !item.isReplacement)
            ?.map((field) => {
                return { name: field.name, id: field.id };
            });
    }

    setCollectorForms(collectorForms: (FormGroup | null)[]): void {
        this._collectorForms.next(collectorForms!);
    }

    getCollectorForms$(): Observable<(FormGroup | null)[]> {
        return this._collectorForms.asObservable();
    }

    getCollectorsFormsValues(): Object {
        const allFormValues =
            this._collectorForms?.value?.map((dynamicForm) => {
                return dynamicForm?.getRawValue();
            }) || [];
        return allFormValues.reduce(
            (acc, val) => ({
                ...acc,
                ...val,
            }),
            {}
        );
    }

    getCollectorFormValueByKey(key: string): string {
        const formsValues = this.getCollectorsFormsValues();
        return formsValues[key] || '';
    }

    isCustomerReferenceRequired(): boolean {
        return this.configService.isCustomerReferenceRequired();
    }

    getStaticFieldsValue(): Object {
        const formsValues = this.getCollectorsFormsValues();
        let staticFieldsValues: Object = {};
        this.customerService.instant_customer_fields?.forEach((customerField) => {
            if (formsValues.hasOwnProperty(customerField.name!)) {
                staticFieldsValues[customerField.name!] = formsValues[customerField.name!];
            }
        });
        return staticFieldsValues;
    }

    getStaticFieldValueByKey(key: string): string {
        const formValues = this.getStaticFieldsValue();
        return formValues[key] || '';
    }

    getReplacementFieldsAndValues(key: string): string {
        const dynamicFormsValues = this.getCollectorsFormsValues();
        const dynamicFields = this.getAllDynamicFields();
        let staticDynamicValues: Object = {};
        dynamicFields.forEach((item) => {
            if (dynamicFormsValues.hasOwnProperty(item.name!) && item.isReplacement) {
                staticDynamicValues[item.name!] = dynamicFormsValues[item.name!];
            }
        });
        return staticDynamicValues[key] || '';
    }

    getDynamicFieldsAndValues(): Object {
        const dynamicFormsValues = this.getCollectorsFormsValues();
        const dynamicFields = this.getAllDynamicFieldsByIdAndNameWithoutReplacement();

        let dynamicValues: Object = {};

        dynamicFields.forEach((dynamicField) => {
            if (dynamicFormsValues.hasOwnProperty(dynamicField.name!)) {
                dynamicValues[dynamicField.id!] = dynamicFormsValues[dynamicField.name!];
            }
        });

        return dynamicValues;
    }
}
